import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { 
  submitEmergencyContact,
  selectContactById,
  selectSubmitLoading
} from '../../store/emergencyContact/emergencyContactSlice';

const EmergencyContactFormComp = ({ onSubmit, onCancel, onBack, remarks, defaultData }) => {
  const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    
    const existingContact = useSelector((state) => selectContactById(state, id));
    const submitLoading = useSelector(selectSubmitLoading);
  
    const [formData, setFormData] = useState({
      contact_name: '',
      relation: '',
      phone: ''
    });
  
    useEffect(() => {
      if (isEditMode && existingContact) {
        setFormData({
          contact_name: existingContact.contact_name || '',
          relation: existingContact.relation || '',
          phone: existingContact.phone || ''
        });
      }
    }, [isEditMode, existingContact]);
  
    const formatPhoneNumber = (value) => {
      // Remove all non-digit characters
      const phoneNumber = value.replace(/\D/g, '');
      
      // Format as +1-XXX-XXX-XXXX for US/Canada
      if (phoneNumber.length === 0) return '';
      if (phoneNumber.length <= 1) return `+${phoneNumber}`;
      if (phoneNumber.length <= 4) return `+${phoneNumber.slice(0, 1)}-${phoneNumber.slice(1)}`;
      if (phoneNumber.length <= 7) return `+${phoneNumber.slice(0, 1)}-${phoneNumber.slice(1, 4)}-${phoneNumber.slice(4)}`;
      return `+${phoneNumber.slice(0, 1)}-${phoneNumber.slice(1, 4)}-${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7, 11)}`;
    };
  
    const handleInputChange = (field, value) => {
      if (field === 'phone') {
        const formattedPhone = formatPhoneNumber(value);
        setFormData(prev => ({
          ...prev,
          [field]: formattedPhone
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [field]: value
        }));
      }
    };
  
    const handleSubmit = async (e) => {
      e.preventDefault();
  
      // Validation
      if (!formData.contact_name.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Required Field',
          text: 'Please provide contact name.'
        });
        return;
      }
  
      if (!formData.relation.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Required Field',
          text: 'Please provide relation.'
        });
        return;
      }
  
      if (!formData.phone.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Required Field',
          text: 'Please provide phone number.'
        });
        return;
      }
  
      // Validate phone has at least 10 digits (US/Canada format)
      const digitsOnly = formData.phone.replace(/\D/g, '');
      if (digitsOnly.length < 11) {
        Swal.fire({
          icon: 'warning',
          title: 'Invalid Phone',
          text: 'Please provide a complete phone number (at least 10 digits).'
        });
        return;
      }
  
      try {
        const submitData = { ...formData };
        
        if (isEditMode) {
          submitData.id = id;
        }
  
        await dispatch(submitEmergencyContact(submitData)).unwrap();
  
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Emergency contact ${isEditMode ? 'updated' : 'added'} successfully!`
        });
  
        navigate('/dashboard/emergency-contact/list');
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error || 'Failed to submit contact'
        });
      }
    };

    return (
      <Container>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div className="header-title">
                  <h4 className="card-title">
                    {isEditMode ? 'Edit' : 'Add'} Emergency Contact
                  </h4>
                </div>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <h5 className="mb-3">Contact Information</h5>
                  <Row className="mb-3">
                    <Col md="6">
                      <Form.Group className="mb-3">
                        <Form.Label>Contact Name *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g., John Doe"
                          value={formData.contact_name}
                          onChange={(e) => handleInputChange('contact_name', e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md="6">
                      <Form.Group className="mb-3">
                        <Form.Label>Relation *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g., Father, Mother, Grandmother, etc."
                          value={formData.relation}
                          onChange={(e) => handleInputChange('relation', e.target.value)}
                          required
                        />
                        <Form.Text className="text-muted">
                          Relationship to child (e.g., Father, Mother, Aunt, Uncle, Grandparent)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
  
                  <Row className="mb-3">
                    <Col md="6">
                      <Form.Group className="mb-3">
                        <Form.Label>Phone Number *</Form.Label>
                        <Form.Control
                          type="tel"
                          placeholder="Start typing phone number..."
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          required
                          maxLength={16}
                        />
                        <Form.Text className="text-muted">
                          Format: +1-XXX-XXX-XXXX (US/Canada)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
  
                  <div className="alert alert-info" role="alert">
                    <i className="icon material-symbols-outlined me-2" style={{verticalAlign: 'middle'}}>
                      info
                    </i>
                    <strong>Note:</strong> Emergency contacts will be called in case of an emergency when primary guardians cannot be reached.
                  </div>
  
                  {/* Submit Buttons */}
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    {/* <Button 
                      variant="secondary" 
                      onClick={() => navigate('/dashboard/emergency-contact/list')}
                      disabled={submitLoading}
                    >
                      Cancel
                    </Button> */}
                    <Button 
                      variant="primary" 
                      type="submit"
                      disabled={submitLoading}
                    >
                      {submitLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Submitting...
                        </>
                      ) : (
                        <>{isEditMode ? 'Update' : 'Add'} Contact</>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
};
export default EmergencyContactFormComp;
